import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl } from "@/lib/api";

const probeDuration = (file) => new Promise((resolve) => {
  if (!/^(video|audio)\//.test(file.type)) return resolve(null);
  const el = document.createElement(file.type.startsWith("video") ? "video" : "audio");
  el.preload = "metadata";
  el.onloadedmetadata = () => { resolve(isFinite(el.duration) ? el.duration : null); URL.revokeObjectURL(el.src); };
  el.onerror = () => resolve(null);
  el.src = URL.createObjectURL(file);
});

const CHUNK = 8 * 1024 * 1024;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const tracker = (total, onProgress) => {
  const t0 = Date.now();
  return (sent) => {
    const secs = (Date.now() - t0) / 1000;
    const speed = secs > 0.5 ? sent / secs : 0;
    onProgress?.({ phase: "uploading", pct: Math.min(99, Math.round((sent / total) * 100)), eta: speed ? Math.round((total - sent) / speed) : null });
  };
};

const chunkedUpload = async (projectId, file, duration, compress, onProgress) => {
  const report = tracker(file.size, onProgress);
  const { data: init } = await api.post("/references/chunked", { project_id: projectId, name: file.name || "reference",
    mime: file.type || "application/octet-stream", size: file.size, duration, compress });
  for (let off = 0; off < file.size; off += CHUNK) {
    for (let attempt = 0; ; attempt++) {
      try {
        await api.put(`/references/chunked/${init.upload_id}?offset=${off}`, file.slice(off, off + CHUNK),
          { headers: { "Content-Type": "application/octet-stream" }, timeout: 300000, onUploadProgress: (e) => report(off + (e.loaded || 0)) });
        break;
      } catch (e) {
        if (attempt >= 3 || [404, 413].includes(e?.response?.status)) throw e;
        await sleep(1500 * (attempt + 1));
      }
    }
  }
  await api.post(`/references/chunked/${init.upload_id}/complete`);
  onProgress?.({ phase: "optimizing", pct: 100, eta: null });
  for (;;) {
    await sleep(2000);
    const { data } = await api.get(`/references/chunked/${init.upload_id}`);
    if (data.status === "done") return data.reference;
    if (data.status === "failed") throw new Error("Upload failed — please try again");
  }
};

// Upload a file to project references; returns attachment {id,url,mime,name,preview,duration}
// App-wide count of running uploads: survives page switches and warns before the tab is closed.
let ACTIVE = 0;
const ACTIVE_SUBS = new Set();
const bump = (d) => { ACTIVE = Math.max(0, ACTIVE + d); ACTIVE_SUBS.forEach((f) => f(ACTIVE)); };
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => { if (ACTIVE > 0) { e.preventDefault(); e.returnValue = "Uploads are still running"; } });
}
export const useActiveUploads = () => {
  const [n, setN] = useState(ACTIVE);
  useEffect(() => { ACTIVE_SUBS.add(setN); return () => ACTIVE_SUBS.delete(setN); }, []);
  return n;
};

export const useUploader = (projectId, onUploaded, compress = true) => {
  const [busy, setBusy] = useState(0);
  const upload = useCallback(async (file, onProgress) => {
    const preview = URL.createObjectURL(file);
    const duration = await probeDuration(file);
    setBusy((b) => b + 1);
    bump(1);
    try {
      let data;
      if (file.size > 20 * 1024 * 1024 || (compress && file.type.startsWith("video/") && file.type !== "video/mp4")) {
        data = await chunkedUpload(projectId, file, duration, compress, onProgress);
      } else {
        const form = new FormData();
        form.append("project_id", projectId);
        form.append("file", file);
        if (duration) form.append("duration", String(duration));
        const report = tracker(file.size, onProgress);
        data = (await api.post("/references", form, { timeout: 120000, onUploadProgress: (e) => report(e.loaded || 0) })).data;
      }
      onUploaded?.(data);
      return { ...data, preview: data.mime && data.mime !== file.type ? mediaUrl(data.url) : preview, duration: duration || data.duration };
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
      return null;
    } finally { setBusy((b) => b - 1); bump(-1); }
  }, [projectId, onUploaded, compress]);
  return { upload, uploading: busy > 0 };
};

// Upload boxes live in a module store so uploads keep running (and show up again) when the user switches pages.
const videoSecs = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  const url = URL.createObjectURL(file);
  v.preload = "metadata";
  v.onloadedmetadata = () => { resolve(Number.isFinite(v.duration) ? v.duration : 0); URL.revokeObjectURL(url); };
  v.onerror = () => { resolve(0); URL.revokeObjectURL(url); };
  v.src = url;
});
const STORE = new Map();
const SUBS = new Map();
const publish = (key, next) => { STORE.set(key, next); (SUBS.get(key) || new Set()).forEach((f) => f(next)); };
export const clearSlots = (projectId, slots) => slots.forEach((s) => publish(`${projectId}:${s}`, []));
const rawSet = (key, upd) => publish(key, typeof upd === "function" ? upd(STORE.get(key) || []) : upd);

// Managed attachment list with instant previews + upload spinners.
export const useAttachments = (projectId, max = 1, onUploaded, compress = true, slot, maxSecs, onReject) => {
  const own = useRef(Math.random().toString(36).slice(2));
  const key = `${projectId}:${slot || own.current}`;
  const [items, setLocal] = useState(() => STORE.get(key) || []);
  useEffect(() => {
    const subs = SUBS.get(key) || new Set();
    subs.add(setLocal);
    SUBS.set(key, subs);
    setLocal(STORE.get(key) || []);
    return () => subs.delete(setLocal);
  }, [key]);
  const live = useRef(onUploaded);
  live.current = onUploaded;
  const { upload } = useUploader(projectId, (d) => live.current?.(d), compress);
  // Restores from saved sessions never drop an upload that is running or finished while the page was closed.
  const setItems = useCallback((upd) => rawSet(key, (cur) => {
    const next = typeof upd === "function" ? upd(cur) : upd;
    const keep = cur.filter((x) => (x.uploading || x.fresh) && !next.some((y) => (y.tid && y.tid === x.tid) || (y.id && y.id === x.id)));
    return [...keep, ...next].slice(0, max).map(({ fresh, ...x }) => x);
  }), [key, max]);
  const add = useCallback(async (files) => {
    let all = Array.from(files).slice(0, Math.max(0, max - (STORE.get(key) || []).length));
    if (maxSecs) {
      const ok = await Promise.all(all.map(async (f) => {
        const secs = f instanceof Blob ? ((f.type || "").startsWith("video/") ? await videoSecs(f) : 0) : Number(f.duration || 0);
        if (secs > maxSecs + 0.05) {
          const msg = `${f.name || "Video"} is ${secs.toFixed(1)} s — max ${maxSecs} s. Trim it to ${maxSecs} seconds or less before uploading.`;
          if (onReject) onReject(msg); else toast.error(msg);
          return false;
        }
        return true;
      }));
      all = all.filter((_, i) => ok[i]);
    }
    const refs = all.filter((f) => !(f instanceof Blob));
    if (refs.length) rawSet(key, (cur) => [...cur, ...refs.filter((r) => !cur.some((x) => x.id === r.id))].slice(0, max));
    const list = all.filter((f) => f instanceof Blob);
    const temps = list.map((f) => ({ tid: Math.random().toString(36).slice(2), preview: URL.createObjectURL(f), mime: f.type, name: f.name, size: f.size, uploading: true, progress: { phase: "uploading", pct: 0, eta: null } }));
    rawSet(key, (cur) => [...cur, ...temps].slice(0, max));
    await Promise.all(list.map(async (f, i) => {
      const t = temps[i];
      const done = await upload(f, (progress) => rawSet(key, (cur) => cur.map((x) => (x.tid === t.tid ? { ...x, progress } : x))));
      const away = !(SUBS.get(key) || new Set()).size;
      rawSet(key, (cur) => done ? cur.map((x) => (x.tid === t.tid ? { ...done, preview: done.mime && done.mime !== t.mime ? done.preview : t.preview, ...(away ? { fresh: true } : {}) } : x)) : cur.filter((x) => x.tid !== t.tid));
      if (done && away) toast.success(`${f.name || "File"} uploaded`, { description: "It's waiting in its upload box." });
    }));
  }, [key, max, upload, maxSecs]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = (i) => rawSet(key, (cur) => cur.filter((_, j) => j !== i));
  const addRef = (ref) => rawSet(key, (cur) => (cur.some((x) => x.id === ref.id) || cur.length >= max ? cur : [...cur, ref]));
  const uploading = items.some((x) => x.uploading);
  const ids = items.filter((x) => x.id).map((x) => x.id);
  return { items, setItems, setRaw: (u) => rawSet(key, u), add, remove, addRef, uploading, ids };
};
